/**
 * Conversion Tags — master page.
 *
 * Two responsibilities:
 *  1. CRUD user-defined tags (qualified, junk, hot, …) — the taxonomy that
 *     CAPI filters / reports / dashboards consult.
 *  2. CRUD GHL → tag mappings so the right tag is attached automatically as
 *     leads flow in (no manual tagging required for the common case).
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Tag as TagIcon,
  Pencil,
  CheckCircle2,
  AlertCircle,
  Copy,
} from "lucide-react";

type Tag = {
  id: string;
  name: string;
  slug: string;
  kind: "qualified" | "disqualified" | "neutral";
  color: string | null;
  description: string | null;
};

/** Dane taga wysylane przy tworzeniu/edycji. */
type TagInput = Pick<Tag, "name" | "kind"> & { color?: string | null; description?: string | null };

/** Dane mapowania wysylane przy tworzeniu/edycji. */
type MappingInput = Pick<Mapping, "tagId" | "source" | "matchType" | "matchValue"> & {
  customField?: string | null;
  enabled?: boolean;
};

type Mapping = {
  id: string;
  tagId: string;
  source: "ghl" | "shopify" | "calendly" | "everwebinar" | "url" | "custom";
  matchType:
    | "pipeline_stage"
    | "tag"
    | "event"
    | "url_contains"
    | "url_exact"
    | "url_regex"
    | "custom_field";
  matchValue: string;
  customField: string | null;
  enabled: boolean;
};

const KIND_INFO: Record<Tag["kind"], { label: string; color: string }> = {
  qualified: { label: "Kwalifikujący", color: "#10b981" },
  disqualified: { label: "Dyskwalifikujący", color: "#ef4444" },
  neutral: { label: "Neutralny", color: "#6b7280" },
};

const SOURCE_LABEL: Record<Mapping["source"], string> = {
  ghl: "LeadConnector (GHL)",
  shopify: "Shopify",
  calendly: "Calendly",
  everwebinar: "EverWebinar",
  url: "Odwiedzona strona (URL)",
  custom: "Custom webhook",
};

const MATCH_TYPE_LABEL: Record<Mapping["matchType"], string> = {
  pipeline_stage: "Pipeline stage",
  tag: "Tag w CRM",
  event: "Typ eventu",
  url_contains: "URL zawiera",
  url_exact: "URL dokładnie",
  url_regex: "URL regex",
  custom_field: "Pole custom",
};

// Source → which matchTypes are valid. Keeps the picker honest (no
// `pipeline_stage` option when the source is `url`).
const MATCH_TYPES_BY_SOURCE: Record<Mapping["source"], Mapping["matchType"][]> = {
  ghl: ["pipeline_stage", "tag", "event"],
  shopify: ["tag", "event"],
  calendly: ["event"],
  everwebinar: ["event"],
  url: ["url_contains", "url_exact", "url_regex"],
  custom: ["tag", "event", "custom_field"],
};

export default function TagsPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { isAgencyUser, selectedClient } = useAgencyClients();
  // Reuse the same per-client scoping as the rest of the app — agency owners
  // managing client A never see client B's taxonomy.
  const forUserId = isAgencyUser ? (selectedClient?.userId ?? null) : null;
  const qs = forUserId ? `?forUserId=${encodeURIComponent(forUserId)}` : "";

  const { data: tags = [], isLoading: tagsLoading } = useQuery<Tag[]>({
    queryKey: ["conversion-tags", forUserId],
    queryFn: () => apiRequest("GET", `/api/conversion-tags/tags${qs}`),
    staleTime: 30_000,
  });

  const { data: mappings = [], isLoading: mappingsLoading } = useQuery<Mapping[]>({
    queryKey: ["conversion-tag-mappings", forUserId],
    queryFn: () => apiRequest("GET", `/api/conversion-tags/mappings${qs}`),
    staleTime: 30_000,
  });

  const { data: ghlDiag } = useQuery<{
    connection: {
      connected: boolean;
      locationId?: string;
      locationName?: string | null;
      connectedAt?: string;
    };
    signals: {
      totalLast30Days: number;
      tagValues: { value: string; count: number; lastSeenAt: string }[];
      stageValues: { value: string; count: number; lastSeenAt: string }[];
      recentSamples: {
        email: string | null;
        ghlContactId: string | null;
        conversionType: string;
        ghlTag: string | null;
        pipelineStage: string | null;
        convertedAt: string;
      }[];
    };
  }>({
    queryKey: ["conversion-tags-diagnostics-ghl", forUserId],
    queryFn: () => apiRequest("GET", `/api/conversion-tags/diagnostics/ghl${qs}`),
    staleTime: 30_000,
  });

  const withForUserId = (payload: Record<string, unknown>) =>
    forUserId ? { ...payload, forUserId } : payload;

  const createTag = useMutation({
    mutationFn: (data: TagInput) =>
      apiRequest("POST", "/api/conversion-tags/tags", withForUserId(data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversion-tags", forUserId] });
      toast({ title: "Tag dodany" });
    },
    onError: () => toast({ title: "Nie udało się dodać taga", variant: "destructive" }),
  });

  const updateTag = useMutation({
    mutationFn: ({ id, ...data }: TagInput & { id: string }) =>
      apiRequest("PUT", `/api/conversion-tags/tags/${id}${qs}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversion-tags", forUserId] });
      toast({ title: "Zapisano" });
    },
  });

  const deleteTag = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/conversion-tags/tags/${id}${qs}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversion-tags", forUserId] });
      qc.invalidateQueries({ queryKey: ["conversion-tag-mappings", forUserId] });
      toast({ title: "Usunięto" });
    },
  });

  const createMapping = useMutation({
    mutationFn: (data: MappingInput) =>
      apiRequest("POST", "/api/conversion-tags/mappings", withForUserId(data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversion-tag-mappings", forUserId] });
      toast({ title: "Mapowanie dodane" });
    },
    onError: () => toast({ title: "Nie udało się dodać mapowania", variant: "destructive" }),
  });

  const updateMapping = useMutation({
    mutationFn: ({ id, ...data }: MappingInput & { id: string }) =>
      apiRequest("PUT", `/api/conversion-tags/mappings/${id}${qs}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversion-tag-mappings", forUserId] }),
  });

  const deleteMapping = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/conversion-tags/mappings/${id}${qs}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversion-tag-mappings", forUserId] });
      toast({ title: "Mapowanie usunięte" });
    },
  });

  const tagById = new Map(tags.map((t) => [t.id, t]));

  return (
    <div className="h-full overflow-y-auto bg-[#030407] text-white/90">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link href="/leads">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Wróć
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <TagIcon className="w-6 h-6" /> Tagi konwersji
            </h1>
            <p className="text-sm text-muted-foreground">
              Twoja taksonomia leadów — filtruje co leci do Meta CAPI i jak liczone są raporty.
            </p>
          </div>
        </div>

        {/* GHL Diagnostics — proof czy GHL faktycznie cokolwiek przysyla */}
        <Card
          className={
            ghlDiag?.connection?.connected
              ? "border-emerald-500/25 bg-emerald-500/[0.02]"
              : "border-amber-500/25 bg-amber-500/[0.02]"
          }
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {ghlDiag?.connection?.connected ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              ) : (
                <AlertCircle className="w-4 h-4 text-amber-400" />
              )}
              Status integracji GHL (LeadConnector)
            </CardTitle>
            <CardDescription>
              Sprawdza co realnie wpada z GHL w ciagu ostatnich 30 dni. Bez tego mapowania to
              wrozenie z fusow.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!ghlDiag ? (
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-md bg-white/[0.03] border border-white/[0.06] p-2.5 space-y-2"
                  >
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-2.5 w-32" />
                  </div>
                ))}
              </div>
            ) : !ghlDiag.connection.connected ? (
              <div className="text-sm text-amber-200">
                GHL nieskonfigurowany.{" "}
                <Link href="/integrations">
                  <a className="text-amber-300 hover:underline">Przejdz do integracji</a>
                </Link>{" "}
                i polacz LeadConnector.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-md bg-white/[0.03] border border-white/[0.06] p-2.5">
                    <p className="text-white/40 mb-1">Polaczona lokalizacja</p>
                    <p className="font-semibold text-white">
                      {ghlDiag.connection.locationName || "(bez nazwy)"}
                    </p>
                    <p className="font-mono text-[10px] text-white/40 mt-0.5">
                      {ghlDiag.connection.locationId}
                    </p>
                  </div>
                  <div className="rounded-md bg-white/[0.03] border border-white/[0.06] p-2.5">
                    <p className="text-white/40 mb-1">Sygnaly GHL z 30 dni</p>
                    <p className="font-mono text-xl font-bold text-white">
                      {ghlDiag.signals.totalLast30Days}
                    </p>
                    <p className="text-[10px] text-white/40 mt-0.5">konwersji z metadanymi GHL</p>
                  </div>
                </div>

                {ghlDiag.signals.totalLast30Days === 0 && (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.05] p-2.5 text-[12px] text-amber-400">
                    <strong>Brak sygnalow.</strong> GHL nie wysyla webhookow do nas LUB
                    pixel-snippet nie chwyta wizyt. Sprawdz: <strong>(1)</strong> webhook URL w GHL
                    automation, <strong>(2)</strong> czy lokalizacja w GHL wysyla zdarzenia{" "}
                    <code>contact.tag_added</code> / <code>pipeline.stage_changed</code>.
                  </div>
                )}

                {ghlDiag.signals.tagValues.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5 font-semibold">
                      Tagi GHL ktore wpadly
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {ghlDiag.signals.tagValues.map((t) => (
                        <button
                          key={t.value}
                          onClick={() => {
                            navigator.clipboard.writeText(t.value);
                            toast({
                              title: "Skopiowano",
                              description: `"${t.value}" — wklej w "Wartosc do matchowania"`,
                            });
                          }}
                          className="group inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.08] hover:border-emerald-400/40 transition-colors text-[11px]"
                          title="Klik aby skopiowac do schowka"
                        >
                          <span className="text-white/85 font-mono">{t.value}</span>
                          <span className="text-[10px] text-white/40">×{t.count}</span>
                          <Copy className="w-3 h-3 text-white/30 group-hover:text-emerald-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {ghlDiag.signals.stageValues.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5 font-semibold">
                      Pipeline stages ktore wpadly
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {ghlDiag.signals.stageValues.map((s) => (
                        <button
                          key={s.value}
                          onClick={() => {
                            navigator.clipboard.writeText(s.value);
                            toast({ title: "Skopiowano" });
                          }}
                          className="group inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.08] hover:border-blue-400/40 transition-colors text-[11px]"
                        >
                          <span className="text-white/85 font-mono">{s.value}</span>
                          <span className="text-[10px] text-white/40">×{s.count}</span>
                          <Copy className="w-3 h-3 text-white/30 group-hover:text-blue-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {ghlDiag.signals.recentSamples.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5 font-semibold">
                      Ostatnie leady z GHL ({ghlDiag.signals.recentSamples.length})
                    </p>
                    <div className="rounded-md border border-white/[0.06] divide-y divide-white/[0.05]">
                      {ghlDiag.signals.recentSamples.map((s, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between gap-3 px-3 py-2 text-[11px]"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-white/85 truncate">{s.email || "(brak email)"}</p>
                            <div className="flex flex-wrap gap-1.5 mt-0.5 text-[10px]">
                              <span className="px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 font-mono">
                                {s.conversionType}
                              </span>
                              {s.ghlTag && (
                                <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-mono">
                                  tag: {s.ghlTag}
                                </span>
                              )}
                              {s.pipelineStage && (
                                <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 font-mono">
                                  stage: {s.pipelineStage}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-white/30 text-[10px] tabular-nums flex-shrink-0">
                            {new Date(s.convertedAt).toLocaleString("pl-PL", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Tagi */}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg">Tagi</CardTitle>
              <CardDescription>
                Definicje. <strong>Slug</strong> (canonical form) jest używany w filtrach CAPI — nie
                zmienia się przy zmianie nazwy.
              </CardDescription>
            </div>
            <AddTagDialog
              onSubmit={(data) => createTag.mutate(data)}
              isLoading={createTag.isPending}
            />
          </CardHeader>
          <CardContent>
            {tagsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Skeleton className="h-5 w-24 rounded-full" />
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-24" />
                    <div className="ml-auto flex gap-1">
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  </div>
                ))}
              </div>
            ) : tags.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Brak tagów. Dodaj pierwszy żeby zacząć filtrować konwersje.
              </p>
            ) : (
              <div className="space-y-2">
                {tags.map((tag) => (
                  <TagRow
                    key={tag.id}
                    tag={tag}
                    onUpdate={(data) => updateTag.mutate({ id: tag.id, ...data })}
                    onDelete={() => {
                      if (
                        confirm(
                          `Usunąć tag "${tag.name}"? Wszystkie powiązane mapowania też znikną.`
                        )
                      ) {
                        deleteTag.mutate(tag.id);
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mapowania */}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg">Mapowania auto-tagowania</CardTitle>
              <CardDescription>
                Gdy lead wpada z integracji (np. GHL pipeline stage = "Customer"), automatycznie
                zostaje oznaczony Twoim tagiem.
              </CardDescription>
            </div>
            <AddMappingDialog
              tags={tags}
              onSubmit={(data) => createMapping.mutate(data)}
              isLoading={createMapping.isPending}
              forUserId={forUserId}
            />
          </CardHeader>
          <CardContent>
            {mappingsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Skeleton className="h-5 w-32 rounded-full" />
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <div className="ml-auto flex items-center gap-2">
                      <Skeleton className="h-5 w-10 rounded-full" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  </div>
                ))}
              </div>
            ) : mappings.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Brak mapowań. Bez nich tagi musisz nadawać ręcznie albo wstrzykiwać przez payload
                trackingu.
              </p>
            ) : (
              <div className="space-y-2">
                {mappings.map((m) => (
                  <MappingRow
                    key={m.id}
                    mapping={m}
                    tag={tagById.get(m.tagId)}
                    onToggle={(enabled) => updateMapping.mutate({ ...m, enabled })}
                    onDelete={() => {
                      if (confirm("Usunąć to mapowanie?")) deleteMapping.mutate(m.id);
                    }}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Where this is used */}
        <Card className="bg-muted/30 border-dashed">
          <CardContent className="pt-6 text-sm text-muted-foreground space-y-2">
            <p>
              <strong className="text-white/90">Gdzie tagi działają:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                Filtry CAPI (Wymagane / Wykluczone tagi w mapowaniu eventu) — decydują które
                konwersje lecą do Meta.
              </li>
              <li>
                Lista leadów w{" "}
                <Link href="/leads">
                  <a className="text-blue-400 hover:underline">Sales Data</a>
                </Link>{" "}
                — widoczne na karcie leada.
              </li>
              <li>
                Raporty atrybucji — segmentacja per tag (qualified vs disqualified konwersje).
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Sub-components ───

function TagRow({
  tag,
  onUpdate,
  onDelete,
}: {
  tag: Tag;
  onUpdate: (data: TagInput) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tag.name);
  const [kind, setKind] = useState<Tag["kind"]>(tag.kind);
  const [color, setColor] = useState(tag.color || KIND_INFO[tag.kind].color);
  const [description, setDescription] = useState(tag.description || "");

  if (editing) {
    return (
      <div className="border rounded-lg p-3 space-y-3 bg-white/[0.02]">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Nazwa</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Rodzaj</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as Tag["kind"])}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="qualified">Kwalifikujący</SelectItem>
                <SelectItem value="disqualified">Dyskwalifikujący</SelectItem>
                <SelectItem value="neutral">Neutralny</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Kolor</Label>
            <Input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 p-1"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Opis (opcjonalnie)</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="np. lead który odbył demo"
          />
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => {
              onUpdate({ name, kind, color, description });
              setEditing(false);
            }}
          >
            Zapisz
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
            Anuluj
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg hover:bg-white/[0.02]">
      <Badge style={{ backgroundColor: tag.color || KIND_INFO[tag.kind].color, color: "#fff" }}>
        {tag.name}
      </Badge>
      <code className="text-xs text-muted-foreground">{tag.slug}</code>
      <span className="text-xs text-muted-foreground">{KIND_INFO[tag.kind].label}</span>
      {tag.description && (
        <span className="text-xs text-muted-foreground truncate">— {tag.description}</span>
      )}
      <div className="ml-auto flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete}>
          <Trash2 className="w-4 h-4 text-red-400" />
        </Button>
      </div>
    </div>
  );
}

function MappingRow({
  mapping,
  tag,
  onToggle,
  onDelete,
}: {
  mapping: Mapping;
  tag: Tag | undefined;
  onToggle: (enabled: boolean) => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg hover:bg-white/[0.02]">
      <Badge variant="outline">{SOURCE_LABEL[mapping.source]}</Badge>
      <span className="text-sm text-muted-foreground">{MATCH_TYPE_LABEL[mapping.matchType]}:</span>
      <code className="text-xs">{mapping.matchValue}</code>
      <span className="text-muted-foreground">→</span>
      {tag ? (
        <Badge style={{ backgroundColor: tag.color || KIND_INFO[tag.kind].color, color: "#fff" }}>
          {tag.name}
        </Badge>
      ) : (
        <Badge variant="destructive">tag usunięty</Badge>
      )}
      <div className="ml-auto flex items-center gap-2">
        <Switch checked={mapping.enabled} onCheckedChange={onToggle} />
        <Button variant="ghost" size="sm" onClick={onDelete}>
          <Trash2 className="w-4 h-4 text-red-400" />
        </Button>
      </div>
    </div>
  );
}

function AddTagDialog({
  onSubmit,
  isLoading,
}: {
  onSubmit: (data: TagInput) => void;
  isLoading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<Tag["kind"]>("neutral");
  const [color, setColor] = useState("#6b7280");

  const reset = () => {
    setName("");
    setKind("neutral");
    setColor("#6b7280");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="w-4 h-4 mr-1" /> Dodaj tag
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nowy tag</DialogTitle>
          <DialogDescription>
            Slug zostanie wygenerowany z nazwy. Jest stały — używany w filtrach.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Nazwa</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="np. Qualified, Junk, Hot lead"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Rodzaj</Label>
              <Select
                value={kind}
                onValueChange={(v) => {
                  setKind(v as Tag["kind"]);
                  setColor(KIND_INFO[v as Tag["kind"]].color);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qualified">Kwalifikujący</SelectItem>
                  <SelectItem value="disqualified">Dyskwalifikujący</SelectItem>
                  <SelectItem value="neutral">Neutralny</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Kolor</Label>
              <Input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 p-1"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={!name.trim() || isLoading}
            onClick={() => {
              onSubmit({ name: name.trim(), kind, color });
              setOpen(false);
              reset();
            }}
          >
            Dodaj tag
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMappingDialog({
  tags,
  onSubmit,
  isLoading,
  forUserId,
}: {
  tags: Tag[];
  onSubmit: (data: MappingInput) => void;
  isLoading: boolean;
  forUserId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [tagId, setTagId] = useState("");
  const [source, setSource] = useState<Mapping["source"]>("ghl");
  const [matchType, setMatchType] = useState<Mapping["matchType"]>("pipeline_stage");
  const [matchValue, setMatchValue] = useState("");

  const qs = forUserId ? `?forUserId=${encodeURIComponent(forUserId)}` : "";
  const { data: ghlData, isLoading: ghlLoading } = useQuery<{
    tags: { name: string; id: string | null }[];
    status: "ok" | "not_connected" | "token_expired" | "missing_scope" | "fetch_failed";
  }>({
    queryKey: ["conversion-tags-ghl-live", forUserId],
    queryFn: () => apiRequest("GET", `/api/conversion-tags/ghl-tags${qs}`),
    staleTime: 30_000,
    enabled: open && source === "ghl",
  });
  const ghlTags = ghlData?.tags ?? [];
  const ghlStatus = ghlData?.status ?? "ok";
  const ghlTagNamesLower = new Set(ghlTags.map((t) => t.name.toLowerCase()));
  const showGhlValidation = source === "ghl" && matchType === "tag";
  const trimmedValue = matchValue.trim().toLowerCase();
  const matchedInGhl =
    showGhlValidation && trimmedValue.length > 0 && ghlTagNamesLower.has(trimmedValue);
  const ghlValueMissing =
    showGhlValidation &&
    trimmedValue.length > 0 &&
    !ghlTagNamesLower.has(trimmedValue) &&
    !ghlLoading &&
    ghlStatus === "ok";

  const reset = () => {
    setTagId("");
    setSource("ghl");
    setMatchType("pipeline_stage");
    setMatchValue("");
  };
  const disabled = !tagId || !matchValue.trim() || isLoading;

  // When source changes, pick the first valid matchType so we never leave the
  // form in an inconsistent state (e.g. source=url + matchType=pipeline_stage).
  const handleSourceChange = (next: Mapping["source"]) => {
    setSource(next);
    const allowed = MATCH_TYPES_BY_SOURCE[next];
    if (!allowed.includes(matchType)) setMatchType(allowed[0]);
    setMatchValue("");
  };

  // Per-source placeholder hints — keeps the dialog self-explanatory without
  // a separate docs panel.
  const placeholder = (() => {
    if (source === "url") {
      if (matchType === "url_regex") return "np. \\/thank-you\\/qualified|\\/show-up";
      if (matchType === "url_exact") return "np. https://twojadomena.pl/thank-you-qualified";
      return "np. /thank-you-qualified, /pricing, ?utm_campaign=hot";
    }
    if (source === "ghl") {
      return matchType === "pipeline_stage"
        ? "np. Customer, Show, NoShow"
        : matchType === "tag"
          ? "np. qualified, hot, junk"
          : "np. opportunity.won";
    }
    return "wartość do matchowania";
  })();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" disabled={tags.length === 0}>
          <Plus className="w-4 h-4 mr-1" /> Dodaj mapowanie
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nowe mapowanie</DialogTitle>
          <DialogDescription>
            Gdy ze źródła wpadnie dany sygnał (CRM, URL strony …), oznacz konwersję tagiem.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Źródło</Label>
            <Select
              value={source}
              onValueChange={(v) => handleSourceChange(v as Mapping["source"])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ghl">LeadConnector (GHL)</SelectItem>
                <SelectItem value="url">Odwiedzona strona (URL)</SelectItem>
                <SelectItem value="shopify" disabled>
                  Shopify (wkrótce)
                </SelectItem>
                <SelectItem value="calendly" disabled>
                  Calendly (wkrótce)
                </SelectItem>
                <SelectItem value="custom">Custom webhook</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Typ matchowania</Label>
            <Select
              value={matchType}
              onValueChange={(v) => setMatchType(v as Mapping["matchType"])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MATCH_TYPES_BY_SOURCE[source].map((mt) => (
                  <SelectItem key={mt} value={mt}>
                    {MATCH_TYPE_LABEL[mt]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {source === "url" && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Reguła sprawdza <strong>wszystkie odwiedzone strony</strong> wizytora (do 200
                ostatnich), nie tylko stronę konwersji. Tag dolatuje do każdej konwersji tego
                wizytora.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Wartość do matchowania (case-insensitive)</Label>
            <Input
              value={matchValue}
              onChange={(e) => setMatchValue(e.target.value)}
              placeholder={placeholder}
            />
            {showGhlValidation && (
              <div className="mt-1.5">
                {ghlLoading ? (
                  <p className="text-[11px] text-muted-foreground">Sprawdzam tagi w GHL...</p>
                ) : ghlStatus === "token_expired" ? (
                  <div className="rounded-md border border-red-500/30 bg-red-500/[0.06] p-2 text-[11px] text-red-200 flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                    <span>
                      Token GHL wygasl. Wejdz w{" "}
                      <Link href="/integrations">
                        <a className="text-red-300 underline">Integracje</a>
                      </Link>{" "}
                      i polacz LeadConnector ponownie. Walidacja tagow wlaczy sie automatycznie.
                    </span>
                  </div>
                ) : ghlStatus === "missing_scope" ? (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] p-2 text-[11px] text-amber-100 flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span>
                      Brakuje uprawnienia{" "}
                      <code className="bg-white/10 px-1 rounded">locations/tags.readonly</code> w
                      polaczeniu GHL. Zostalo dodane do aplikacji — wejdz w{" "}
                      <Link href="/integrations">
                        <a className="text-amber-300 underline">Integracje</a>
                      </Link>
                      , rozlacz LeadConnector i polacz ponownie, zeby przyznac nowy zakres.
                    </span>
                  </div>
                ) : ghlStatus === "not_connected" ? (
                  <p className="text-[11px] text-muted-foreground">
                    GHL nie polaczony — walidacja niedostepna.
                  </p>
                ) : ghlStatus === "fetch_failed" ? (
                  <p className="text-[11px] text-amber-200">
                    Nie udalo sie pobrac tagow z GHL. Sprobuj za chwile.
                  </p>
                ) : matchedInGhl ? (
                  <p className="text-[11px] text-emerald-300 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Tag <code className="bg-white/10 px-1 rounded">{matchValue.trim()}</code>{" "}
                    istnieje w GHL ✓
                  </p>
                ) : ghlValueMissing ? (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] p-2 text-[11px] text-amber-100/85 space-y-1.5">
                    <div className="flex items-start gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>
                        Tag <code className="bg-white/10 px-1 rounded">{matchValue.trim()}</code>{" "}
                        nie istnieje w GHL location. Mapping moze nie matchowac. Dostepne tagi:
                      </span>
                    </div>
                    {ghlTags.length > 0 ? (
                      <div className="flex flex-wrap gap-1 pl-5">
                        {ghlTags.map((t) => (
                          <button
                            key={t.id ?? t.name}
                            type="button"
                            onClick={() => setMatchValue(t.name)}
                            className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.1] text-white/80 hover:border-emerald-400/40 hover:bg-emerald-500/10 hover:text-emerald-100 text-[10px] font-mono transition-colors"
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="pl-5 text-white/40 italic">Brak tagow w GHL location.</p>
                    )}
                  </div>
                ) : ghlTags.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-[10px] text-emerald-300/60 uppercase tracking-wider font-semibold">
                      Tagi z Twojego GHL (klik aby wybrac):
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {ghlTags.map((t) => (
                        <button
                          key={t.id ?? t.name}
                          type="button"
                          onClick={() => setMatchValue(t.name)}
                          className="px-1.5 py-0.5 rounded bg-emerald-500/[0.05] border border-emerald-500/20 text-emerald-200 hover:bg-emerald-500/15 hover:border-emerald-400/50 text-[10px] font-mono transition-colors"
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Przypisz tag</Label>
            <Select value={tagId} onValueChange={setTagId}>
              <SelectTrigger>
                <SelectValue placeholder="Wybierz tag" />
              </SelectTrigger>
              <SelectContent>
                {tags.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <Badge
                      style={{ backgroundColor: t.color || KIND_INFO[t.kind].color, color: "#fff" }}
                    >
                      {t.name}
                    </Badge>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {KIND_INFO[t.kind].label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={disabled}
            onClick={() => {
              onSubmit({ tagId, source, matchType, matchValue: matchValue.trim(), enabled: true });
              setOpen(false);
              reset();
            }}
          >
            Dodaj mapowanie
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
